import { forwardRef, type HTMLAttributes, type ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { CameraState, ViewerConfig, ViewerGraph, ViewerOperationResult, ViewerStats, ViewerThemeTokens, ViewportInsets, ViewportSnapshot, VisibleNode } from "../index";
import { defaultConfig, defaultTheme } from "../index";
import type { ViewerController } from "../viewer";
import { ViewerProvider } from "./context";
import { getReactRuntimeFactories } from "./runtime";
const ZERO_VIEWPORT_INSETS: Readonly<Required<ViewportInsets>> = Object.freeze({
  top: 0,
  right: 0,
  bottom: 0,
  left: 0
});
export interface GraphCanvasHandle {
  fitView(animate?: boolean, signal?: AbortSignal): Promise<ViewerOperationResult>;
  resetView(animate?: boolean): void;
  focusNode(id: string, animate?: boolean): boolean;
  centerOn(x: number, y: number, animate?: boolean): void;
  zoomIn(animate?: boolean): void;
  zoomOut(animate?: boolean): void;
  zoomBy(factor: number, animate?: boolean): void;
  screenToWorld(x: number, y: number): {
    x: number;
    y: number;
  } | null;
  worldToScreen(x: number, y: number): {
    x: number;
    y: number;
  } | null;
  getCameraState(): Readonly<CameraState> | null;
  getVisibleNodes(): readonly VisibleNode[];
  getViewport(): ViewportSnapshot | null;
  getStats(): ViewerStats | null;
}
export interface GraphCanvasProps extends Omit<HTMLAttributes<HTMLElement>, "children" | "onSelect" | "role"> {
  graph: ViewerGraph;
  selectedId: string | null;
  hoveredId?: string | null;
  highlightedIds?: Iterable<string> | null;
  attentionIds?: Iterable<string> | null;
  visibleEdgeKinds?: Iterable<string> | null;
  config?: Partial<ViewerConfig>;
  theme?: Partial<ViewerThemeTokens>;
  viewportInsets?: ViewportInsets;
  children?: ReactNode;
  onSelectionChange?: (selectedId: string | null) => void;
  onPaneClick?: () => void;
  onHoverChange?: (hoveredId: string | null) => void;
  onViewportChange?: (viewport: ViewportSnapshot) => void;
  onStats?: (stats: ViewerStats) => void;
}
export const GraphCanvas = forwardRef<GraphCanvasHandle, GraphCanvasProps>(function GraphCanvas({
  graph,
  selectedId,
  hoveredId,
  highlightedIds = null,
  attentionIds = null,
  visibleEdgeKinds = null,
  config,
  theme,
  viewportInsets,
  children,
  className,
  style,
  "aria-label": ariaLabel = "Graph viewer",
  onSelectionChange,
  onPaneClick,
  onHoverChange,
  onViewportChange,
  onStats,
  ...divProps
}, ref) {
  const hostRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<ViewerController | null>(null);
  const [controller, setController] = useState<ViewerController | null>(null);
  const controlledRef = useRef({
    selectedId,
    hoveredId
  });
  controlledRef.current = {
    selectedId,
    hoveredId
  };
  const callbacksRef = useRef({
    onSelectionChange,
    onPaneClick,
    onHoverChange,
    onViewportChange,
    onStats
  });
  callbacksRef.current = {
    onSelectionChange,
    onPaneClick,
    onHoverChange,
    onViewportChange,
    onStats
  };
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const viewer = getReactRuntimeFactories().createViewer(host);
    const unsubscribers = [viewer.subscribe("selection", ({
      selectedId: next,
      source
    }) => {
      if (source !== "interaction") return;
      callbacksRef.current.onSelectionChange?.(next);
      if (next === null) callbacksRef.current.onPaneClick?.();
      viewer.setSelection(controlledRef.current.selectedId);
    }), viewer.subscribe("hover", ({
      hoveredId: next_0,
      source: source_0
    }) => {
      if (source_0 !== "interaction") return;
      callbacksRef.current.onHoverChange?.(next_0);
      if (controlledRef.current.hoveredId !== undefined) {
        viewer.setHover(controlledRef.current.hoveredId);
      }
    }), viewer.subscribe("viewport", next_1 => callbacksRef.current.onViewportChange?.(next_1)), viewer.subscribe("stats", next_2 => callbacksRef.current.onStats?.(next_2))];
    controllerRef.current = viewer;
    setController(viewer);
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
      controllerRef.current = null;
      viewer.dispose();
    };
  }, []);
  useEffect(() => controllerRef.current?.setGraph(graph), [graph]);
  useEffect(() => controllerRef.current?.setSelection(selectedId), [selectedId]);
  useEffect(() => {
    if (hoveredId !== undefined) controllerRef.current?.setHover(hoveredId);
  }, [hoveredId]);
  useEffect(() => controllerRef.current?.setHighlights(highlightedIds), [highlightedIds]);
  useEffect(() => controllerRef.current?.setAttention(attentionIds), [attentionIds]);
  useEffect(() => controllerRef.current?.setVisibleEdgeKinds(visibleEdgeKinds), [visibleEdgeKinds]);
  useEffect(() => {
    controllerRef.current?.setConfig({
      ...defaultConfig,
      ...config
    });
  }, [config]);
  useEffect(() => {
    controllerRef.current?.setTheme({
      ...defaultTheme,
      ...theme
    });
  }, [theme]);
  useEffect(() => {
    controllerRef.current?.setViewportInsets(viewportInsets ?? ZERO_VIEWPORT_INSETS);
  }, [viewportInsets]);
  useImperativeHandle(ref, () => ({
    fitView: (animate, signal) => controllerRef.current?.fitView(animate, signal) ?? Promise.resolve("unavailable"),
    resetView: animate_0 => controllerRef.current?.resetView(animate_0),
    focusNode: (id, animate_1) => controllerRef.current?.focusNode(id, animate_1) ?? false,
    centerOn: (x, y, animate_2) => controllerRef.current?.centerOn(x, y, animate_2),
    zoomIn: animate_3 => controllerRef.current?.zoomIn(animate_3),
    zoomOut: animate_4 => controllerRef.current?.zoomOut(animate_4),
    zoomBy: (factor, animate_5) => controllerRef.current?.zoomBy(factor, animate_5),
    screenToWorld: (x_0, y_0) => controllerRef.current?.screenToWorld(x_0, y_0) ?? null,
    worldToScreen: (x_1, y_1) => controllerRef.current?.worldToScreen(x_1, y_1) ?? null,
    getCameraState: () => controllerRef.current?.getCameraState() ?? null,
    getVisibleNodes: () => controllerRef.current?.getVisibleNodes() ?? [],
    getViewport: () => controllerRef.current?.getViewport() ?? null,
    getStats: () => controllerRef.current?.getStats() ?? null
  }), []);
  return <section {...divProps} className={className} style={{
    ...style,
    position: style?.position ?? "relative"
  }} aria-label={ariaLabel}>
        <div ref={hostRef} aria-hidden="true" style={{
      position: "absolute",
      inset: 0
    }} />
        {controller ? <ViewerProvider viewer={controller}>{children}</ViewerProvider> : null}
      </section>;
});